import { useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { ActivityMessage, ChatAppRequest, ServerMessage } from "@/api/models"
import type { Edge, Node } from "@xyflow/react"

import { explorerChatWebSocketApi } from "@/api/api"
import { VectorFieldOptions } from "@/api/models"
import { Loader2, Plus, Rocket, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useUpsertWorkflow } from "@/features/ai-explorer/hooks/use-upsert-workflow"
import { api } from "@/lib/api-client"
import { safeImport } from "@/lib/chunk-reload"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import { useWorkflowAiChatStore } from "../stores/workflow-ai-chat-store"
import { useWorkflowStore } from "../stores/workflow-store"
import { applyWorkflowOperations, type WorkflowOperation } from "../utils/apply-workflow-operations"
import { buildWorkflowBuilderUserMessage } from "../utils/build-workflow-builder-message"
import { canCreateFlows } from "../utils/can-create-flows"
import { buildAutoRetryPrompt } from "../utils/workflow-ai-retry"
import { layoutWorkflowGraph } from "../utils/workflow-auto-layout"
import { getWorkflowIssues, type WorkflowIssue } from "../utils/workflow-validation"

async function generateWorkflowTitle(prompt: string): Promise<string> {
  try {
    const response = (await api.post<{ title: string }>("/explorer/workflow/generate-title", {
      prompt,
    })) as unknown as { title: string }
    return response.title || "New Flow"
  } catch {
    return "New Flow"
  }
}

const DEFAULT_START_NODE = {
  id: "start-1",
  type: "start",
  position: { x: 0, y: 0 },
  data: { label: "Start" },
}

export function FlowsAiPrompt() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [activityMessage, setActivityMessage] = useState("")
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasAppliedEditsRef = useRef(false)
  const operationErrorsRef = useRef<string[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const upsertWorkflowMutation = useUpsertWorkflow()
  const applyGraphUpdate = useWorkflowStore((state) => state.applyGraphUpdate)
  const requestAutoLayout = useWorkflowStore((state) => state.requestAutoLayout)
  const setIsOpen = useWorkflowAiChatStore((state) => state.setIsOpen)
  const setPendingPrompt = useWorkflowAiChatStore((state) => state.setPendingPrompt)

  const handleServerMessage = useCallback(
    (args: {
      message: ServerMessage
      workflowId: string
      nodesRef: React.MutableRefObject<unknown[]>
      edgesRef: React.MutableRefObject<unknown[]>
    }) => {
      const { message, workflowId, nodesRef, edgesRef } = args
      if (message.type !== "activity") return

      const activityMsg = message as ActivityMessage

      if (activityMsg.activity_type === "workflow_edit") {
        const details = activityMsg.details
        const targetWorkflowId =
          typeof details?.workflow_id === "string" ? details.workflow_id : null
        if (!targetWorkflowId || targetWorkflowId !== workflowId) return

        const rawOperations = details?.operations
        if (!Array.isArray(rawOperations)) return

        const result = applyWorkflowOperations(
          rawOperations as WorkflowOperation[],
          nodesRef.current as Parameters<typeof applyWorkflowOperations>[1],
          edgesRef.current as Parameters<typeof applyWorkflowOperations>[2]
        )

        operationErrorsRef.current = result.errors
        if (result.errors.length > 0) {
          toast.error("Some AI changes could not be applied", {
            description: result.errors[0],
          })
        }

        nodesRef.current = result.nodes
        edgesRef.current = result.edges
        hasAppliedEditsRef.current = true
        setActivityMessage("")
        return
      }

      const translatedMessage = translateActivityMessage(activityMsg, t)
      if (translatedMessage) {
        setActivityMessage(translatedMessage)
      }
    },
    [t]
  )

  const handleCreateWithAi = useCallback(async () => {
    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt) return

    setIsCreating(true)
    setActivityMessage("")
    hasAppliedEditsRef.current = false
    operationErrorsRef.current = []
    setPendingPrompt(null)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const initialContent = {
        nodes: [DEFAULT_START_NODE],
        edges: [],
      }

      // Start title generation in parallel with workflow creation
      const titlePromise = generateWorkflowTitle(trimmedPrompt)

      const workflowResult = await upsertWorkflowMutation.mutateAsync({
        title: "New Flow", // Temporary title while AI generates the real one
        content: initialContent,
        file_required: false,
        visibility: "personal",
      })

      const workflowId = workflowResult.id
      const nodesRef = { current: [DEFAULT_START_NODE] as unknown[] }
      const edgesRef = { current: [] as unknown[] }

      const settingsService = SettingsService.getInstance()
      const token = settingsService.token

      const runBuilderRequest = async (userPrompt: string, issues: WorkflowIssue[]) => {
        const outgoingUserContent = buildWorkflowBuilderUserMessage({
          workflowId,
          userPrompt,
          nodes: nodesRef.current as Node[],
          edges: edgesRef.current as Edge[],
          issues,
          interactionMode: "one_shot_create",
        })

        const request: ChatAppRequest = {
          messages: [{ role: "user", content: outgoingUserContent }],
          context: {
            overrides: {
              agent_name: "workflow_builder",
              use_custom_gpt: true,
              custom_gpt_model: "auto",
              vector_fields: [VectorFieldOptions.Embedding],
            },
          },
          stream: true,
          session_state: null,
        }

        setActivityMessage(t("workflowLanding.aiPrompt.generating"))

        const response = await explorerChatWebSocketApi(
          request,
          undefined,
          token,
          abortController.signal,
          (msg) => handleServerMessage({ message: msg, workflowId, nodesRef, edgesRef }),
          "open",
          null
        )

        if (!response.body) {
          throw new Error("No response body")
        }

        const { default: readNDJSONStream } = await safeImport(
          () => import("ndjson-readablestream")
        )

        for await (const event of readNDJSONStream(response.body)) {
          if (!event) continue
          if (abortController.signal.aborted) {
            const abortError = new Error("AbortError")
            abortError.name = "AbortError"
            throw abortError
          }

          if (event?.error || event?.type === "error") {
            const errorMessage = String(event?.message || event?.error || "Unknown error")
            throw new Error(errorMessage)
          }
        }
      }

      let currentIssues = getWorkflowIssues(nodesRef.current as Node[], edgesRef.current as Edge[])

      await runBuilderRequest(trimmedPrompt, currentIssues)

      /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated in streaming callback */
      if (!hasAppliedEditsRef.current) {
        throw new Error("AI did not return any workflow edits.")
      }

      currentIssues = getWorkflowIssues(nodesRef.current as Node[], edgesRef.current as Edge[])
      const errorIssues = currentIssues.filter((issue) => issue.severity === "error")
      const operationErrors = operationErrorsRef.current
      const hasErrors = operationErrors.length > 0 || errorIssues.length > 0

      const layoutedResult = layoutWorkflowGraph(
        nodesRef.current as Parameters<typeof layoutWorkflowGraph>[0],
        edgesRef.current as Parameters<typeof layoutWorkflowGraph>[1],
        { direction: "LR" }
      )

      // Ensure title generation has completed
      const finalTitle = await titlePromise

      await upsertWorkflowMutation.mutateAsync({
        id: workflowId,
        title: finalTitle,
        content: {
          nodes: layoutedResult.nodes,
          edges: layoutedResult.edges,
        },
        file_required: false,
        visibility: "personal",
      })

      applyGraphUpdate(layoutedResult.nodes, layoutedResult.edges)
      requestAutoLayout()

      if (hasErrors) {
        const retryPrompt = buildAutoRetryPrompt(operationErrors, errorIssues)
        setPendingPrompt(retryPrompt)
        setIsOpen(true)
        toast.info(t("workflowLanding.aiPrompt.createdWithIssues"), {
          description: t("workflowLanding.aiPrompt.autoFixing"),
        })
      } else {
        toast.success(t("workflowLanding.aiPrompt.created"))
      }
      void navigate(`/flows/${workflowId}?autolayout=true`)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (message !== "AbortError") {
        toast.error(t("workflowLanding.aiPrompt.error"), { description: message })
      }
    } finally {
      setIsCreating(false)
      setActivityMessage("")
      abortControllerRef.current = null
    }
  }, [
    prompt,
    upsertWorkflowMutation,
    handleServerMessage,
    applyGraphUpdate,
    navigate,
    requestAutoLayout,
    setIsOpen,
    setPendingPrompt,
    t,
  ])

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsCreating(false)
    setActivityMessage("")
  }, [])

  const handleCreateBlank = useCallback(() => {
    void navigate("/flows/new")
  }, [navigate])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !isCreating) {
      event.preventDefault()
      void handleCreateWithAi()
    }
  }

  const handleBoxClick = () => {
    textareaRef.current?.focus()
  }

  // Fas 1a: hide the AI flow creator when creation is restricted to admins.
  if (!canCreateFlows()) {
    return null
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl items-center gap-4">
      {/* AI Flow Creator - Chat-style input */}
      <div
        role="presentation"
        className={cn(
          "bg-card flex-1 cursor-text rounded-lg border shadow-sm",
          "transition-shadow duration-300",
          "focus-within:shadow-primary/10 focus-within:ring-primary/30 focus-within:shadow-lg focus-within:ring-1"
        )}
        onClick={handleBoxClick}
      >
        <Textarea
          ref={textareaRef}
          disabled={isCreating}
          placeholder={t("workflowLanding.aiPrompt.placeholder")}
          rows={3}
          value={prompt}
          className={cn(
            "max-h-[200px] min-h-[80px] resize-none rounded-t-lg border-0 bg-transparent px-4 pt-4 pb-2",
            "placeholder:text-muted-foreground/50 text-base shadow-none focus-visible:ring-0"
          )}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="flex items-center justify-between px-3 pb-3">
          <div className="text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="size-3.5" />
            <span className="text-xs">{t("workflowLanding.aiPrompt.aiPowered")}</span>
          </div>
          <div className="flex items-center gap-2">
            {isCreating && activityMessage && (
              <span className="animate-shimmer from-muted-foreground via-foreground to-muted-foreground bg-linear-to-r bg-size-[200%_100%] bg-clip-text text-xs text-transparent">
                {activityMessage}
              </span>
            )}
            {isCreating ? (
              <Button className="h-8 rounded-lg" size="sm" variant="outline" onClick={handleCancel}>
                {t("workflowLanding.aiPrompt.cancel")}
              </Button>
            ) : null}
            <Button
              aria-label={t("workflowLanding.aiPrompt.submit")}
              className="h-8 rounded-lg"
              disabled={isCreating || !prompt.trim()}
              size="sm"
              onClick={() => void handleCreateWithAi()}
            >
              {isCreating ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Rocket className="size-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Or separator */}
      <div className="text-muted-foreground/50 flex flex-col items-center gap-1">
        <div className="bg-border h-6 w-px" />
        <span className="text-xs font-medium">{t("workflowLanding.or")}</span>
        <div className="bg-border h-6 w-px" />
      </div>

      {/* Blank Flow Option */}
      <button
        type="button"
        className={cn(
          "card-interactive group bg-card flex h-full min-h-[120px] w-40 flex-col items-center justify-center gap-2 rounded-lg border p-4 shadow-sm",
          "hover:border-border",
          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
        )}
        onClick={handleCreateBlank}
      >
        <div className="bg-muted group-hover:bg-primary/10 flex size-10 items-center justify-center rounded-full transition-colors">
          <Plus className="text-muted-foreground group-hover:text-primary size-5 transition-colors" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium">{t("workflowLanding.blankFlow.title")}</p>
          <p className="text-muted-foreground text-xs">{t("workflowLanding.blankFlow.subtitle")}</p>
        </div>
      </button>
    </div>
  )
}
